import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, MessageSquare, Mail, Copy, Loader2, Inbox } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { useLang } from "../lib/i18n";
import { label, statusColor } from "../lib/constants";
import { smsLink, mailLink } from "../lib/helpers";
import { FieldAI } from "./ai/FieldAI";

export const PageHeader = ({ title, subtitle, actions, back }) => {
  const nav = useNavigate();
  return (
    <div className="flex flex-wrap items-start justify-between gap-3 mb-5 fade-up">
      <div className="flex items-start gap-2 min-w-0">
        {back && (
          <button data-testid="page-back-btn" onClick={() => nav(-1)} className="btn-ghost btn-sm -ml-2 px-2" aria-label="Back">
            <ArrowLeft className="w-5 h-5" />
          </button>
        )}
        <div className="min-w-0">
          <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900 break-words" data-testid="page-title">{title}</h1>
          {subtitle && <div className="text-sm text-slate-500 mt-1">{subtitle}</div>}
        </div>
      </div>
      {actions && <div className="flex flex-wrap gap-2">{actions}</div>}
    </div>
  );
};

export const Section = ({ title, action, children, testid, count }) => (
  <section className="card overflow-hidden fade-up" data-testid={testid}>
    {title && (
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 bg-slate-50/60">
        <h2 className="text-base font-bold text-slate-900 flex items-center gap-2">
          {title}
          {count != null && <span className="mono text-xs bg-slate-900 text-white rounded-full px-2 py-0.5">{count}</span>}
        </h2>
        {action}
      </div>
    )}
    {children}
  </section>
);

export const StatusBadge = ({ status, testid }) => {
  const { lang } = useLang();
  return (
    <span data-testid={testid} className={`inline-flex items-center whitespace-nowrap rounded-full border px-2.5 py-0.5 text-xs font-semibold ${statusColor(status)}`}>
      {label(status, lang)}
    </span>
  );
};

export const Field = ({ label: l, children, className = "", ai }) => (
  <label className={`block ${className}`}>
    <span className="label">{l}{ai && <FieldAI label={l} {...ai} />}</span>
    {children}
  </label>
);

export const Empty = ({ text, testid }) => (
  <div className="flex flex-col items-center justify-center gap-2 py-8 text-slate-400 text-sm" data-testid={testid}>
    <Inbox className="w-6 h-6" />
    {text}
  </div>
);

export const Loading = () => (
  <div className="flex justify-center py-16 text-slate-400" data-testid="loading">
    <Loader2 className="w-7 h-7 animate-spin" />
  </div>
);

export const RowLink = ({ to, children, testid }) => (
  <Link to={to} className="row-link" data-testid={testid}>
    {children}
  </Link>
);

export const Options = ({ items, lang }) =>
  items.map(([k, en, es]) => (
    <option key={k} value={k}>
      {lang === "es" ? es : en}
    </option>
  ));

export function SendResultDialog({ data, onClose }) {
  const { t } = useLang();
  if (!data) return null;
  const copy = () => {
    navigator.clipboard?.writeText(data.message + (data.link && !data.message.includes(data.link) ? `\n${data.link}` : ""));
    toast.success(t("Copied", "Copiado"));
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="send-result-dialog">
        <DialogHeader>
          <DialogTitle>{t("Message ready", "Mensaje listo")}</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-slate-500">
          {t("Saved in the customer's communication history. Tap to send from your phone:", "Guardado en el historial del cliente. Toque para enviarlo desde su teléfono:")}
        </p>
        <div className="rounded-xl bg-slate-50 border p-3 text-sm whitespace-pre-wrap" data-testid="send-result-message">{data.message}</div>
        <div className="grid gap-2">
          <a className="btn-primary" href={smsLink(data.phone, data.message)} data-testid="send-result-sms-btn">
            <MessageSquare className="w-5 h-5" /> {t("Send Text (SMS)", "Enviar mensaje de texto")} {data.phone ? `· ${data.phone}` : ""}
          </a>
          <a className="btn-outline" href={mailLink(data.email, data.subject, data.message)} data-testid="send-result-email-btn">
            <Mail className="w-5 h-5" /> {t("Send Email", "Enviar correo")} {data.email ? `· ${data.email}` : ""}
          </a>
          <button className="btn-ghost" onClick={copy} data-testid="send-result-copy-btn">
            <Copy className="w-5 h-5" /> {t("Copy text", "Copiar texto")}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function useBusy() {
  const [busy, setBusy] = useState(false);
  const run = async (fn) => {
    setBusy(true);
    try {
      return await fn();
    } finally {
      setBusy(false);
    }
  };
  return [busy, run];
}
